import React, { useContext, useEffect, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
// material
import {
  Stack,
  Container,
  Typography,
  Card,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Divider,
  Fab,
  Link,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import editFill from '@iconify/icons-eva/edit-2-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
// components
import VoucherFormDialog from './form/dialog';
// contexts
import { EventContext } from '../../contexts/Event';
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// services
import { listVouchers, deleteVoucher } from '../../services/Voucher';

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  border: `1px dashed ${grey[300]}`
}));

export default function VoucherList() {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [focusVoucher, setFocusVoucher] = useState();
  const [vouchers, setVouchers] = useState([]);
  const [openVoucherFormDialog, setOpenVoucherFormDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };
        const vouchers = await listVouchers({ headers, eventId: event._id });
        if (vouchers.length) {
          setVouchers(vouchers);
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setHasError(true);
      }
    };

    if (event?._id) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [event, authUser, authSettings]);

  const handleOpenDialog = (voucher) => {
    setFocusVoucher(voucher);
    setOpenVoucherFormDialog(true);
  };

  const handleDelete = async (voucher) => {
    if (!window.confirm(`Do you really want to delete this voucher: "${voucher.code}"?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      await deleteVoucher({ headers, voucherId: voucher._id });
      setVouchers((vouchers) =>
        vouchers.filter((currentVoucher) => currentVoucher._id !== voucher._id)
      );
    } catch (err) {
      console.error(err);
      alert('An error occurred! Please, try again.');
    }

    setIsLoading(false);
  };

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">An error occurred! Please, refresh the page and try again.</Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4">Vouchers</Typography>
          {vouchers.length > 0 && (
            <Fab
              size="medium"
              color="primary"
              aria-label="add"
              sx={{ flexShrink: 0 }}
              onClick={handleOpenDialog}
            >
              <Icon icon={plusFill} width="24" />
            </Fab>
          )}
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          If you like to create a voucher to a <strong>specific ticket</strong>. {'Got to '}
          <Link to={`/ticket/list?eventId=${event._id}`} color="inherit" component={RouterLink}>
            ticket page
          </Link>
          {' and add a dicount rule!'}
        </Alert>

        {vouchers.length ? (
          <Card>
            <Box sx={{ p: { xs: 1, sm: 3 } }}>
              <List>
                {vouchers.map((voucher, i) => (
                  <Box key={voucher._id}>
                    {i > 0 && <Divider component="li" />}
                    <ListItem
                      secondaryAction={
                        <>
                          <IconButton aria-label="edit" onClick={() => handleOpenDialog(voucher)}>
                            <Icon icon={editFill} />
                          </IconButton>
                          {voucher.applied > 0 ? (
                            <Tooltip
                              title="You cannot delete this voucher because it has already been used"
                              placement="bottom-end"
                            >
                              <span>
                                <IconButton aria-label="delete" disabled>
                                  <Icon icon={trashFill} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <IconButton aria-label="delete" onClick={() => handleDelete(voucher)}>
                              <Icon icon={trashFill} />
                            </IconButton>
                          )}
                        </>
                      }
                    >
                      <ListItemText
                        primary={voucher.code}
                        secondary={`Discount ${voucher.discount}%`}
                      />
                      <ListItemText secondary={`${voucher.applied} / ${voucher.limit}`} />
                    </ListItem>
                  </Box>
                ))}
              </List>
            </Box>
          </Card>
        ) : (
          <CardStyle>
            <Box sx={{ textAlign: 'center', p: 5 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                No vouchers registered!
                <Typography
                  component="span"
                  variant="body2"
                  color="grey.500"
                  sx={{ display: 'block' }}
                >
                  Add your first voucher
                </Typography>
              </Typography>
              <Fab
                size="medium"
                color="primary"
                aria-label="add"
                sx={{ flexShrink: 0 }}
                onClick={handleOpenDialog}
              >
                <Icon icon={plusFill} width="24" />
              </Fab>
            </Box>
          </CardStyle>
        )}
      </Container>

      <VoucherFormDialog
        openVoucherFormDialog={openVoucherFormDialog}
        setOpenVoucherFormDialog={setOpenVoucherFormDialog}
        setVouchers={setVouchers}
        voucher={focusVoucher}
      />
    </>
  );
}
